import { RelayEmailAction, RelayEmailResult } from "./relayEmail.ts";
import * as Context from "effect/Context";
import * as Schema from "effect/Schema";
import * as HttpApi from "effect/unstable/httpapi/HttpApi";
import * as HttpApiEndpoint from "effect/unstable/httpapi/HttpApiEndpoint";
import * as HttpApiError from "effect/unstable/httpapi/HttpApiError";
import * as HttpApiGroup from "effect/unstable/httpapi/HttpApiGroup";
import * as HttpApiMiddleware from "effect/unstable/httpapi/HttpApiMiddleware";
import * as HttpApiSchema from "effect/unstable/httpapi/HttpApiSchema";
import * as HttpApiSecurity from "effect/unstable/httpapi/HttpApiSecurity";
import * as OpenApi from "effect/unstable/httpapi/OpenApi";

import { EnvironmentId, ThreadId, TrimmedNonEmptyString } from "./baseSchemas.ts";
import { AuthUserProfile } from "./auth.ts";
import {
  RelaySourceControlAction,
  RelaySourceControlResult,
  RelaySourceControlError,
} from "./relaySourceControl.ts";
import { ExecutionEnvironmentDescriptor } from "./environment.ts";
export * from "./cloudEnvironment.ts";
import {
  CloudEnvironment,
  CloudEnvironmentActionRequest,
  CloudEnvironmentConflict,
  CloudEnvironmentCreateRequest,
  CloudEnvironmentForbidden,
  CloudEnvironmentNotFound,
  CloudEnrollmentCredential,
  CloudEnrollmentInvalid,
  CloudEnrollmentReadyRequest,
  CloudEnrollmentRequest,
  CloudUpdaterDesiredRequest,
  CloudUpdaterDesiredResponse,
  CloudUpdaterReportRequest,
  CloudUpdaterRequestHeaders,
} from "./cloudEnvironment.ts";

export const RelayAgentAwarenessPlatform = Schema.Literal("ios");
export type RelayAgentAwarenessPlatform = typeof RelayAgentAwarenessPlatform.Type;

export const RelayAgentAwarenessPhase = Schema.Literals([
  "starting",
  "running",
  "waiting_for_approval",
  "waiting_for_input",
  "completed",
  "failed",
  "stale",
]);
export type RelayAgentAwarenessPhase = typeof RelayAgentAwarenessPhase.Type;

export const RelayAgentAwarenessPreferences = Schema.Struct({
  liveActivitiesEnabled: Schema.Boolean,
  notificationsEnabled: Schema.Boolean,
  notifyOnApproval: Schema.Boolean,
  notifyOnInput: Schema.Boolean,
  notifyOnCompletion: Schema.Boolean,
  notifyOnFailure: Schema.Boolean,
});
export type RelayAgentAwarenessPreferences = typeof RelayAgentAwarenessPreferences.Type;

export const RelayApnsEnvironment = Schema.Literals(["sandbox", "production"]);
export type RelayApnsEnvironment = typeof RelayApnsEnvironment.Type;

export const RelayDeviceRegistrationRequest = Schema.Struct({
  deviceId: TrimmedNonEmptyString,
  label: TrimmedNonEmptyString,
  platform: RelayAgentAwarenessPlatform,
  iosMajorVersion: Schema.Int.check(Schema.isGreaterThanOrEqualTo(18)),
  appVersion: Schema.optional(TrimmedNonEmptyString),
  // APNs routing for this install: the topic must match the app's bundle id
  // (dev/preview/prod variants differ) and development-signed builds receive
  // sandbox tokens. Optional so older app builds keep registering; the relay
  // falls back to its configured defaults.
  bundleId: Schema.optional(TrimmedNonEmptyString),
  apsEnvironment: Schema.optional(RelayApnsEnvironment),
  pushToken: Schema.optional(TrimmedNonEmptyString),
  pushToStartToken: Schema.optional(TrimmedNonEmptyString),
  preferences: RelayAgentAwarenessPreferences,
});
export type RelayDeviceRegistrationRequest = typeof RelayDeviceRegistrationRequest.Type;

export const RelayClientDeviceRecord = Schema.Struct({
  deviceId: TrimmedNonEmptyString,
  label: TrimmedNonEmptyString,
  platform: RelayAgentAwarenessPlatform,
  iosMajorVersion: Schema.Int.check(Schema.isGreaterThanOrEqualTo(18)),
  appVersion: Schema.NullOr(TrimmedNonEmptyString),
  notifications: Schema.Struct({
    enabled: Schema.Boolean,
    notifyOnApproval: Schema.Boolean,
    notifyOnInput: Schema.Boolean,
    notifyOnCompletion: Schema.Boolean,
    notifyOnFailure: Schema.Boolean,
  }),
  liveActivities: Schema.Struct({
    enabled: Schema.Boolean,
  }),
  updatedAt: TrimmedNonEmptyString,
});
export type RelayClientDeviceRecord = typeof RelayClientDeviceRecord.Type;

export const RelayListDevicesResponse = Schema.Struct({
  devices: Schema.Array(RelayClientDeviceRecord),
});
export type RelayListDevicesResponse = typeof RelayListDevicesResponse.Type;

export const RelayLiveActivityRegistrationRequest = Schema.Struct({
  deviceId: TrimmedNonEmptyString,
  activityPushToken: TrimmedNonEmptyString,
});
export type RelayLiveActivityRegistrationRequest = typeof RelayLiveActivityRegistrationRequest.Type;

export const RelayDeviceUnregistrationParams = Schema.Struct({
  deviceId: TrimmedNonEmptyString,
});
export type RelayDeviceUnregistrationParams = typeof RelayDeviceUnregistrationParams.Type;

export const RelayAgentActivityState = Schema.Struct({
  environmentId: EnvironmentId,
  threadId: ThreadId,
  projectTitle: TrimmedNonEmptyString,
  threadTitle: TrimmedNonEmptyString,
  phase: RelayAgentAwarenessPhase,
  headline: TrimmedNonEmptyString,
  detail: Schema.optional(TrimmedNonEmptyString),
  modelTitle: TrimmedNonEmptyString,
  updatedAt: TrimmedNonEmptyString,
  deepLink: TrimmedNonEmptyString,
});
export type RelayAgentActivityState = typeof RelayAgentActivityState.Type;

export const RelayAgentActivityAggregateRow = Schema.Struct({
  environmentId: EnvironmentId,
  threadId: ThreadId,
  projectTitle: TrimmedNonEmptyString,
  threadTitle: TrimmedNonEmptyString,
  modelTitle: TrimmedNonEmptyString,
  phase: RelayAgentAwarenessPhase,
  status: TrimmedNonEmptyString,
  updatedAt: TrimmedNonEmptyString,
  deepLink: TrimmedNonEmptyString,
});
export type RelayAgentActivityAggregateRow = typeof RelayAgentActivityAggregateRow.Type;

export const RelayAgentActivityAggregateState = Schema.Struct({
  title: TrimmedNonEmptyString,
  subtitle: TrimmedNonEmptyString,
  activeCount: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  updatedAt: TrimmedNonEmptyString,
  activities: Schema.Array(RelayAgentActivityAggregateRow),
});
export type RelayAgentActivityAggregateState = typeof RelayAgentActivityAggregateState.Type;

export const RelayManagedEndpointProviderKind = Schema.Literals([
  "manual",
  "cloudflare_tunnel",
  "maito_relay",
]);
export type RelayManagedEndpointProviderKind = typeof RelayManagedEndpointProviderKind.Type;

export const RelayManagedEndpoint = Schema.Struct({
  httpBaseUrl: TrimmedNonEmptyString,
  wsBaseUrl: TrimmedNonEmptyString,
  providerKind: RelayManagedEndpointProviderKind,
});
export type RelayManagedEndpoint = typeof RelayManagedEndpoint.Type;

export const RelayManagedEndpointOrigin = Schema.Struct({
  localHttpHost: TrimmedNonEmptyString,
  localHttpPort: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(1),
    Schema.isLessThanOrEqualTo(65_535),
  ),
});
export type RelayManagedEndpointOrigin = typeof RelayManagedEndpointOrigin.Type;

export const RelayManagedEndpointRuntimeConfig = Schema.Struct({
  providerKind: RelayManagedEndpointProviderKind,
  connectorToken: TrimmedNonEmptyString,
  tunnelId: Schema.optional(TrimmedNonEmptyString),
  tunnelName: Schema.optional(TrimmedNonEmptyString),
});
export type RelayManagedEndpointRuntimeConfig = typeof RelayManagedEndpointRuntimeConfig.Type;

export const RelayLinkProofRequest = Schema.Struct({
  challenge: Schema.String,
  relayIssuer: Schema.String,
  endpoint: RelayManagedEndpoint,
  origin: RelayManagedEndpointOrigin,
});
export type RelayLinkProofRequest = typeof RelayLinkProofRequest.Type;

export const RelayEnvironmentConfigRequest = Schema.Struct({
  relayUrl: Schema.String,
  relayIssuer: Schema.optional(Schema.String),
  cloudUserId: Schema.String,
  environmentCredential: Schema.String,
  cloudMintPublicKey: Schema.String,
  endpoint: Schema.optional(RelayManagedEndpoint),
  endpointRuntime: Schema.NullOr(RelayManagedEndpointRuntimeConfig),
});
export type RelayEnvironmentConfigRequest = typeof RelayEnvironmentConfigRequest.Type;

export const RelayOAuthAttemptCreateRequest = Schema.Struct({
  projectId: TrimmedNonEmptyString.check(Schema.isMaxLength(191)),
  connectorName: TrimmedNonEmptyString.check(Schema.isMaxLength(191)),
  providerState: TrimmedNonEmptyString.check(Schema.isMaxLength(4_096)),
});
export type RelayOAuthAttemptCreateRequest = typeof RelayOAuthAttemptCreateRequest.Type;

export const RelayOAuthAttemptCreateResponse = Schema.Struct({
  state: TrimmedNonEmptyString.check(Schema.isPattern(/^[0-9a-f]{64}$/u)),
  callbackBaseUrl: TrimmedNonEmptyString,
});
export type RelayOAuthAttemptCreateResponse = typeof RelayOAuthAttemptCreateResponse.Type;

const RelaySignedJwtRegisteredClaims = {
  iss: TrimmedNonEmptyString,
  aud: TrimmedNonEmptyString,
  sub: TrimmedNonEmptyString,
  jti: TrimmedNonEmptyString,
  iat: Schema.Int,
  exp: Schema.Int,
} as const;

export const RelayAgentActivityPublishProofPayload = Schema.Struct({
  ...RelaySignedJwtRegisteredClaims,
  environmentId: EnvironmentId,
  threadId: ThreadId,
  state: Schema.NullOr(RelayAgentActivityState),
});
export type RelayAgentActivityPublishProofPayload =
  typeof RelayAgentActivityPublishProofPayload.Type;
export type RelayAgentActivityPublishProof = string;

export const RelayAgentActivityPublishRequest = Schema.Struct({
  state: Schema.NullOr(RelayAgentActivityState).annotate({
    description: "Current agent-awareness state, or null to remove the published state.",
  }),
  proof: TrimmedNonEmptyString.annotate({
    description: "Environment-signed JWT covering this published activity state.",
  }),
}).annotate({ description: "Publishes a signed agent-awareness update from an environment." });
export type RelayAgentActivityPublishRequest = typeof RelayAgentActivityPublishRequest.Type;

export const RelayEnvironmentLinkScope = Schema.Literals([
  "agent_activity_notifications",
  "managed_tunnels",
]);
export type RelayEnvironmentLinkScope = typeof RelayEnvironmentLinkScope.Type;

export const RelayOrganizationRole = Schema.Literals(["admin", "member"]);
export type RelayOrganizationRole = typeof RelayOrganizationRole.Type;

export const RelayOrganizationPlan = Schema.Literals(["none", "personal", "team"]);
export type RelayOrganizationPlan = typeof RelayOrganizationPlan.Type;

export const RelayEnvironmentLinkProofPayload = Schema.Struct({
  ...RelaySignedJwtRegisteredClaims,
  challenge: TrimmedNonEmptyString,
  descriptor: ExecutionEnvironmentDescriptor,
  environmentId: EnvironmentId,
  environmentPublicKey: TrimmedNonEmptyString,
  endpoint: RelayManagedEndpoint,
  origin: RelayManagedEndpointOrigin,
  scopes: Schema.Array(RelayEnvironmentLinkScope),
});
export type RelayEnvironmentLinkProofPayload = typeof RelayEnvironmentLinkProofPayload.Type;

export const RelayEnvironmentLinkProof = TrimmedNonEmptyString;
export type RelayEnvironmentLinkProof = typeof RelayEnvironmentLinkProof.Type;

export const RelayEnvironmentLinkChallengeRequest = Schema.Struct({
  notificationsEnabled: Schema.Boolean.annotate({
    description: "Whether this link may deliver push notifications.",
  }),
  liveActivitiesEnabled: Schema.Boolean.annotate({
    description: "Whether this link may update Live Activities.",
  }),
  managedTunnelsEnabled: Schema.Boolean.annotate({
    description: "Whether the relay should provision a managed tunnel for this environment.",
  }),
}).annotate({ description: "Requested capabilities for a new environment-link challenge." });
export type RelayEnvironmentLinkChallengeRequest = typeof RelayEnvironmentLinkChallengeRequest.Type;

export const RelayEnvironmentLinkChallengeResponse = Schema.Struct({
  challenge: TrimmedNonEmptyString,
  expiresAt: TrimmedNonEmptyString,
});
export type RelayEnvironmentLinkChallengeResponse =
  typeof RelayEnvironmentLinkChallengeResponse.Type;

export const RelayEnvironmentLinkRequest = Schema.Struct({
  deviceId: Schema.optional(
    TrimmedNonEmptyString.annotate({
      description: "Optional client device identifier associated with this link.",
    }),
  ),
  proof: RelayEnvironmentLinkProof.annotate({
    description: "Environment-signed proof bound to a previously issued link challenge.",
  }),
  notificationsEnabled: Schema.Boolean,
  liveActivitiesEnabled: Schema.Boolean,
  managedTunnelsEnabled: Schema.Boolean,
}).annotate({ description: "Links an authenticated cloud user to a Maito environment." });
export type RelayEnvironmentLinkRequest = typeof RelayEnvironmentLinkRequest.Type;

export const RelayEnvironmentLinkResponse = Schema.Struct({
  ok: Schema.Boolean,
  cloudUserId: TrimmedNonEmptyString,
  environmentId: EnvironmentId,
  endpoint: RelayManagedEndpoint,
  endpointRuntime: Schema.NullOr(RelayManagedEndpointRuntimeConfig),
  relayIssuer: TrimmedNonEmptyString,
  environmentCredential: TrimmedNonEmptyString,
  cloudMintPublicKey: TrimmedNonEmptyString,
});
export type RelayEnvironmentLinkResponse = typeof RelayEnvironmentLinkResponse.Type;

export const RelayEnvironmentLinkProofInvalidReason = Schema.Literals([
  "invalid_signature_or_scope",
  "descriptor_mismatch",
  "replayed_nonce",
  "challenge_invalid",
  "origin_not_allowed",
  "endpoint_not_secure",
]);
export type RelayEnvironmentLinkProofInvalidReason =
  typeof RelayEnvironmentLinkProofInvalidReason.Type;

export const RelayEnvironmentLinkFailedReason = Schema.Literals([
  "link_persistence_failed",
  "credential_persistence_failed",
  "replay_persistence_failed",
  "internal_error",
]);
export type RelayEnvironmentLinkFailedReason = typeof RelayEnvironmentLinkFailedReason.Type;

export const RelayEnvironmentLinkUnavailableReason = Schema.Literals([
  "managed_endpoint_not_configured",
  "managed_endpoint_provisioning_failed",
]);
export type RelayEnvironmentLinkUnavailableReason =
  typeof RelayEnvironmentLinkUnavailableReason.Type;

export const RelayEnvironmentEndpointUnavailableReason = Schema.Literals([
  "endpoint_request_failed",
  "endpoint_response_invalid",
]);
export type RelayEnvironmentEndpointUnavailableReason =
  typeof RelayEnvironmentEndpointUnavailableReason.Type;

export const RelayAgentActivityPublishProofInvalidReason = Schema.Literals([
  "invalid_signature_or_payload",
  "replayed_nonce",
]);
export type RelayAgentActivityPublishProofInvalidReason =
  typeof RelayAgentActivityPublishProofInvalidReason.Type;

export const RelayAuthInvalidReason = Schema.Literals([
  "missing_bearer",
  "invalid_bearer",
  "invalid_dpop",
  "not_authorized",
]);
export type RelayAuthInvalidReason = typeof RelayAuthInvalidReason.Type;

export const RelayOrganizationAccessDeniedReason = Schema.Literals([
  "organization_required",
  "billing_required",
  "membership_required",
  "admin_required",
  "ownership_change_required",
  "environment_owned_by_another_organization",
  "organization_already_has_environment",
]);
export type RelayOrganizationAccessDeniedReason = typeof RelayOrganizationAccessDeniedReason.Type;

export const RelayInternalErrorReason = Schema.Literals([
  "database_unavailable",
  "persistence_failed",
  "upstream_unavailable",
  "internal_error",
]);
export type RelayInternalErrorReason = typeof RelayInternalErrorReason.Type;

export class RelayAuthInvalidError extends Schema.TaggedErrorClass<RelayAuthInvalidError>()(
  "RelayAuthInvalidError",
  {
    code: Schema.Literal("auth_invalid"),
    reason: RelayAuthInvalidReason,
    traceId: TrimmedNonEmptyString,
  },
  { httpApiStatus: 401 },
) {
  override get message(): string {
    return `Relay authentication failed: ${this.reason}`;
  }
}

export class RelayOrganizationAccessDeniedError extends Schema.TaggedErrorClass<RelayOrganizationAccessDeniedError>()(
  "RelayOrganizationAccessDeniedError",
  {
    code: Schema.Literal("organization_access_denied"),
    reason: RelayOrganizationAccessDeniedReason,
    traceId: TrimmedNonEmptyString,
  },
  { httpApiStatus: 403 },
) {
  override get message(): string {
    return `Relay organization access denied: ${this.reason}`;
  }
}

export class RelayEnvironmentLinkProofExpiredError extends Schema.TaggedErrorClass<RelayEnvironmentLinkProofExpiredError>()(
  "RelayEnvironmentLinkProofExpiredError",
  {
    code: Schema.Literal("environment_link_proof_expired"),
    traceId: TrimmedNonEmptyString,
  },
  { httpApiStatus: 401 },
) {
  override get message(): string {
    return "Relay environment link proof expired";
  }
}

export class RelayEnvironmentLinkProofInvalidError extends Schema.TaggedErrorClass<RelayEnvironmentLinkProofInvalidError>()(
  "RelayEnvironmentLinkProofInvalidError",
  {
    code: Schema.Literal("environment_link_proof_invalid"),
    reason: RelayEnvironmentLinkProofInvalidReason,
    traceId: TrimmedNonEmptyString,
  },
  { httpApiStatus: 400 },
) {
  override get message(): string {
    return `Relay environment link proof is invalid: ${this.reason}`;
  }
}

export const RelayEnvironmentConnectNotAuthorizedReason = Schema.Literals([
  "client_proof_key_thumbprint_missing",
  "environment_link_not_found",
  "endpoint_provider_not_managed",
  "managed_endpoint_allocation_not_found",
  "managed_endpoint_base_domain_not_configured",
  "managed_endpoint_allocation_not_ready",
  "managed_endpoint_hostname_invalid",
  "managed_endpoint_mismatch",
  "environment_upgrade_required",
]);
export type RelayEnvironmentConnectNotAuthorizedReason =
  typeof RelayEnvironmentConnectNotAuthorizedReason.Type;

export class RelayEnvironmentConnectNotAuthorizedError extends Schema.TaggedErrorClass<RelayEnvironmentConnectNotAuthorizedError>()(
  "RelayEnvironmentConnectNotAuthorizedError",
  {
    code: Schema.Literal("environment_connect_not_authorized"),
    // Optional so responses from relays deployed before the reason was
    // threaded through still decode.
    reason: Schema.optional(RelayEnvironmentConnectNotAuthorizedReason),
    traceId: TrimmedNonEmptyString,
  },
  { httpApiStatus: 403 },
) {
  override get message(): string {
    return this.reason
      ? `Relay environment connection is not authorized: ${this.reason}`
      : "Relay environment connection is not authorized";
  }
}

export class RelayEnvironmentEndpointUnavailableError extends Schema.TaggedErrorClass<RelayEnvironmentEndpointUnavailableError>()(
  "RelayEnvironmentEndpointUnavailableError",
  {
    code: Schema.Literal("environment_endpoint_unavailable"),
    reason: RelayEnvironmentEndpointUnavailableReason,
    traceId: TrimmedNonEmptyString,
  },
  { httpApiStatus: 502 },
) {
  override get message(): string {
    return `Relay environment endpoint is unavailable: ${this.reason}`;
  }
}

export class RelayEnvironmentEndpointTimedOutError extends Schema.TaggedErrorClass<RelayEnvironmentEndpointTimedOutError>()(
  "RelayEnvironmentEndpointTimedOutError",
  {
    code: Schema.Literal("environment_endpoint_timed_out"),
    traceId: TrimmedNonEmptyString,
  },
  { httpApiStatus: 504 },
) {
  override get message(): string {
    return "Relay environment endpoint request timed out";
  }
}

export class RelayEnvironmentLinkFailedError extends Schema.TaggedErrorClass<RelayEnvironmentLinkFailedError>()(
  "RelayEnvironmentLinkFailedError",
  {
    code: Schema.Literal("environment_link_failed"),
    reason: RelayEnvironmentLinkFailedReason,
    traceId: TrimmedNonEmptyString,
  },
  { httpApiStatus: 500 },
) {
  override get message(): string {
    return `Relay environment link failed: ${this.reason}`;
  }
}

export class RelayEnvironmentLinkUnavailableError extends Schema.TaggedErrorClass<RelayEnvironmentLinkUnavailableError>()(
  "RelayEnvironmentLinkUnavailableError",
  {
    code: Schema.Literal("environment_link_unavailable"),
    reason: RelayEnvironmentLinkUnavailableReason,
    traceId: TrimmedNonEmptyString,
  },
  { httpApiStatus: 503 },
) {
  override get message(): string {
    return `Relay environment link is unavailable: ${this.reason}`;
  }
}

export class RelayEnvironmentLinkLimitExceededError extends Schema.TaggedErrorClass<RelayEnvironmentLinkLimitExceededError>()(
  "RelayEnvironmentLinkLimitExceededError",
  {
    code: Schema.Literal("environment_link_limit_exceeded"),
    maxTunnels: Schema.Number,
    traceId: TrimmedNonEmptyString,
  },
  { httpApiStatus: 403 },
) {
  override get message(): string {
    return `Relay managed tunnel limit reached: this account allows at most ${this.maxTunnels} tunnels`;
  }
}

export class RelayAgentActivityPublishProofExpiredError extends Schema.TaggedErrorClass<RelayAgentActivityPublishProofExpiredError>()(
  "RelayAgentActivityPublishProofExpiredError",
  {
    code: Schema.Literal("agent_activity_publish_proof_expired"),
    traceId: TrimmedNonEmptyString,
  },
  { httpApiStatus: 401 },
) {
  override get message(): string {
    return "Relay agent activity publish proof expired";
  }
}

export class RelayAgentActivityPublishProofInvalidError extends Schema.TaggedErrorClass<RelayAgentActivityPublishProofInvalidError>()(
  "RelayAgentActivityPublishProofInvalidError",
  {
    code: Schema.Literal("agent_activity_publish_proof_invalid"),
    reason: RelayAgentActivityPublishProofInvalidReason,
    traceId: TrimmedNonEmptyString,
  },
  { httpApiStatus: 401 },
) {
  override get message(): string {
    return `Relay agent activity publish proof is invalid: ${this.reason}`;
  }
}

export class RelayInternalError extends Schema.TaggedErrorClass<RelayInternalError>()(
  "RelayInternalError",
  {
    code: Schema.Literal("internal_error"),
    reason: RelayInternalErrorReason,
    traceId: TrimmedNonEmptyString,
  },
  { httpApiStatus: 500 },
) {
  override get message(): string {
    return `Relay internal error: ${this.reason}`;
  }
}

export class RelayPlatformAdminRequiredError extends Schema.TaggedErrorClass<RelayPlatformAdminRequiredError>()(
  "RelayPlatformAdminRequiredError",
  {
    code: Schema.Literal("platform_admin_required"),
  },
  { httpApiStatus: 403 },
) {
  override get message(): string {
    return "Platform administrator access is required";
  }
}

export const RelayProtectedError = Schema.Union([
  RelayAuthInvalidError,
  RelayOrganizationAccessDeniedError,
  RelayEnvironmentLinkProofExpiredError,
  RelayEnvironmentLinkProofInvalidError,
  RelayEnvironmentConnectNotAuthorizedError,
  RelayEnvironmentEndpointUnavailableError,
  RelayEnvironmentEndpointTimedOutError,
  RelayEnvironmentLinkFailedError,
  RelayEnvironmentLinkUnavailableError,
  RelayEnvironmentLinkLimitExceededError,
  RelayAgentActivityPublishProofExpiredError,
  RelayAgentActivityPublishProofInvalidError,
  RelayInternalError,
]);
export type RelayProtectedError = typeof RelayProtectedError.Type;

const RelayAuthAndInternalErrors = [
  RelayAuthInvalidError,
  RelayOrganizationAccessDeniedError,
  RelayInternalError,
] as const;

const RelayEnvironmentLinkErrors = [
  RelayAuthInvalidError,
  RelayOrganizationAccessDeniedError,
  RelayEnvironmentLinkProofExpiredError,
  RelayEnvironmentLinkProofInvalidError,
  RelayEnvironmentLinkUnavailableError,
  RelayEnvironmentLinkLimitExceededError,
  RelayEnvironmentLinkFailedError,
  RelayInternalError,
] as const;

const RelayEnvironmentConnectErrors = [
  RelayAuthInvalidError,
  RelayOrganizationAccessDeniedError,
  RelayEnvironmentConnectNotAuthorizedError,
  RelayEnvironmentEndpointUnavailableError,
  RelayEnvironmentEndpointTimedOutError,
  RelayInternalError,
] as const;

export class RelayClientPrincipal extends Context.Service<
  RelayClientPrincipal,
  {
    readonly userId: string;
    readonly token: string;
    /** Active Clerk organization context for every managed relay session. */
    readonly organizationId: string;
    readonly organizationRole: RelayOrganizationRole;
    readonly proofKeyThumbprint?: string;
    readonly dpopScopes?: ReadonlyArray<RelayDpopAccessTokenScope>;
  }
>()("@maito/contracts/relay/RelayClientPrincipal") {}

export class RelayAccountPrincipal extends Context.Service<
  RelayAccountPrincipal,
  {
    readonly userId: string;
    readonly token: string;
  }
>()("@maito/contracts/relay/RelayAccountPrincipal") {}

export class RelayEnvironmentPrincipal extends Context.Service<
  RelayEnvironmentPrincipal,
  {
    readonly environmentId: string;
    readonly environmentPublicKey: string;
  }
>()("@maito/contracts/relay/RelayEnvironmentPrincipal") {}

const RelayClientBearerAuthorization = HttpApiSecurity.http({ scheme: "bearer" }).pipe(
  HttpApiSecurity.annotate(
    OpenApi.Description,
    "Clerk session bearer token for the signed-in Maito Connect user.",
  ),
);

export class RelayClientAuth extends HttpApiMiddleware.Service<
  RelayClientAuth,
  { provides: RelayClientPrincipal }
>()("RelayClientAuth", {
  error: RelayAuthInvalidError,
  security: { clientBearer: RelayClientBearerAuthorization },
}) {}

export class RelayAccountAuth extends HttpApiMiddleware.Service<
  RelayAccountAuth,
  { provides: RelayAccountPrincipal }
>()("RelayAccountAuth", {
  error: RelayAuthInvalidError,
  security: { accountBearer: RelayClientBearerAuthorization },
}) {}

const RelayEnvironmentBearerAuthorization = HttpApiSecurity.http({ scheme: "bearer" }).pipe(
  HttpApiSecurity.annotate(
    OpenApi.Description,
    "Relay-issued environment credential installed when the environment is linked.",
  ),
);

export class RelayEnvironmentAuth extends HttpApiMiddleware.Service<
  RelayEnvironmentAuth,
  { provides: RelayEnvironmentPrincipal }
>()("RelayEnvironmentAuth", {
  error: [RelayAuthInvalidError, RelayInternalError],
  security: { environmentBearer: RelayEnvironmentBearerAuthorization },
}) {}

const RelayDpopAuthorization = HttpApiSecurity.http({ scheme: "DPoP" }).pipe(
  HttpApiSecurity.annotate(
    OpenApi.Description,
    "DPoP-bound access token. Requests must also include the DPoP proof JWT header.",
  ),
);

export class RelayDpopClientAuth extends HttpApiMiddleware.Service<
  RelayDpopClientAuth,
  { provides: RelayClientPrincipal }
>()("RelayDpopClientAuth", {
  error: RelayAuthInvalidError,
  security: { relayDpop: RelayDpopAuthorization },
}) {}

export const RelayClientEnvironmentRecord = Schema.Struct({
  environmentId: EnvironmentId,
  label: TrimmedNonEmptyString,
  endpoint: RelayManagedEndpoint,
  linkedAt: TrimmedNonEmptyString,
  organizationId: Schema.optionalKey(TrimmedNonEmptyString),
});
export type RelayClientEnvironmentRecord = typeof RelayClientEnvironmentRecord.Type;

export const RelayListEnvironmentsResponse = Schema.Struct({
  environments: Schema.Array(RelayClientEnvironmentRecord),
});
export type RelayListEnvironmentsResponse = typeof RelayListEnvironmentsResponse.Type;

export const RelayWorkspaceInvitationRequest = Schema.Struct({
  emailAddress: TrimmedNonEmptyString,
  role: Schema.Literals(["org:admin", "org:member"]),
});
export type RelayWorkspaceInvitationRequest = typeof RelayWorkspaceInvitationRequest.Type;

export const RelayEnvironmentConnectRequest = Schema.Struct({
  deviceId: Schema.optional(
    TrimmedNonEmptyString.annotate({
      description: "Optional client device identifier requesting the connection.",
    }),
  ),
  clientKeyThumbprint: Schema.optional(
    TrimmedNonEmptyString.annotate({
      description: "Deprecated alias for clientProofKeyThumbprint.",
    }),
  ),
  clientProofKeyThumbprint: Schema.optional(
    TrimmedNonEmptyString.annotate({
      description: "JWK thumbprint that the minted environment credential must be bound to.",
    }),
  ),
}).annotate({ description: "Requests a short-lived credential for connecting to an environment." });
export type RelayEnvironmentConnectRequest = typeof RelayEnvironmentConnectRequest.Type;

export const RelayEnvironmentConnectScope = "environment:connect" as const;
export const RelayEnvironmentStatusScope = "environment:status" as const;
export const RelayDpopAccessTokenScope = Schema.Literals([
  RelayEnvironmentConnectScope,
  RelayEnvironmentStatusScope,
]);
export type RelayDpopAccessTokenScope = typeof RelayDpopAccessTokenScope.Type;

export const RelayDpopTokenExchangeGrantType =
  "urn:ietf:params:oauth:grant-type:token-exchange" as const;
export const RelayJwtSubjectTokenType = "urn:ietf:params:oauth:token-type:jwt" as const;
export const RelayAccessTokenType = "urn:ietf:params:oauth:token-type:access_token" as const;
export const RelayPublicClientId = Schema.Literals(["maito-web"]);
export type RelayPublicClientId = typeof RelayPublicClientId.Type;
export const RelayWebClientId = "maito-web" as const;

export const RelayDpopAccessTokenRequest = Schema.Struct({
  grant_type: Schema.Literal(RelayDpopTokenExchangeGrantType),
  subject_token: TrimmedNonEmptyString.annotate({
    description: "Clerk bearer token for the signed-in cloud user.",
  }),
  subject_token_type: Schema.Literal(RelayJwtSubjectTokenType),
  requested_token_type: Schema.Literal(RelayAccessTokenType),
  resource: TrimmedNonEmptyString.annotate({
    description: "Relay issuer URL that will receive the DPoP-bound access token.",
  }),
  scope: TrimmedNonEmptyString.annotate({
    description: "Space-separated relay scopes requested by the client.",
  }),
  client_id: RelayPublicClientId,
})
  .annotate({ description: "OAuth token exchange request for a DPoP-bound relay access token." })
  .pipe(HttpApiSchema.asFormUrlEncoded());
export type RelayDpopAccessTokenRequest = typeof RelayDpopAccessTokenRequest.Type;

export const RelayDpopAccessTokenResponse = Schema.Struct({
  access_token: TrimmedNonEmptyString,
  issued_token_type: Schema.Literal(RelayAccessTokenType),
  token_type: Schema.Literal("DPoP"),
  expires_in: Schema.Int.check(Schema.isGreaterThan(0)),
  scope: TrimmedNonEmptyString,
});
export type RelayDpopAccessTokenResponse = typeof RelayDpopAccessTokenResponse.Type;

export const RelayBearerRequestHeaders = Schema.Struct({
  authorization: TrimmedNonEmptyString,
});

export const RelayDpopProofRequestHeaders = Schema.Struct({
  dpop: TrimmedNonEmptyString,
});

export const RelayDpopRequestHeaders = Schema.Struct({
  authorization: TrimmedNonEmptyString,
  dpop: TrimmedNonEmptyString,
});

export const RelayAuthorizationServerMetadata = Schema.Struct({
  issuer: TrimmedNonEmptyString,
  token_endpoint: TrimmedNonEmptyString,
  grant_types_supported: Schema.Array(Schema.Literal(RelayDpopTokenExchangeGrantType)),
  token_endpoint_auth_methods_supported: Schema.Array(Schema.Literal("none")),
  dpop_signing_alg_values_supported: Schema.Array(Schema.Literal("ES256")),
  scopes_supported: Schema.Array(RelayDpopAccessTokenScope),
});

export const RelayProtectedResourceMetadata = Schema.Struct({
  resource: TrimmedNonEmptyString,
  authorization_servers: Schema.Array(TrimmedNonEmptyString),
  scopes_supported: Schema.Array(RelayDpopAccessTokenScope),
  dpop_bound_access_tokens_required: Schema.Boolean,
  dpop_signing_alg_values_supported: Schema.Array(Schema.Literal("ES256")),
});

export const RelayEnvironmentUnlinkParams = Schema.Struct({
  environmentId: EnvironmentId,
});
export type RelayEnvironmentUnlinkParams = typeof RelayEnvironmentUnlinkParams.Type;

export const RelayEnvironmentRenameRequest = Schema.Struct({
  label: TrimmedNonEmptyString,
});
export type RelayEnvironmentRenameRequest = typeof RelayEnvironmentRenameRequest.Type;

export const RelayEnvironmentConnectResponse = Schema.Struct({
  environmentId: EnvironmentId,
  endpoint: RelayManagedEndpoint,
  credential: TrimmedNonEmptyString,
  expiresAt: TrimmedNonEmptyString,
  descriptor: Schema.optionalKey(ExecutionEnvironmentDescriptor),
});
export type RelayEnvironmentConnectResponse = typeof RelayEnvironmentConnectResponse.Type;

export const RelayEnvironmentStatusValue = Schema.Literals(["online", "offline"]);
export type RelayEnvironmentStatusValue = typeof RelayEnvironmentStatusValue.Type;

export const RelayEnvironmentStatusResponse = Schema.Struct({
  environmentId: EnvironmentId,
  endpoint: RelayManagedEndpoint,
  status: RelayEnvironmentStatusValue,
  checkedAt: TrimmedNonEmptyString,
  descriptor: Schema.optional(ExecutionEnvironmentDescriptor),
  error: Schema.optional(TrimmedNonEmptyString),
  traceId: Schema.optional(TrimmedNonEmptyString),
});
export type RelayEnvironmentStatusResponse = typeof RelayEnvironmentStatusResponse.Type;

export const RelayCloudMintCredentialProofPayload = Schema.Struct({
  ...RelaySignedJwtRegisteredClaims,
  requesterUserId: TrimmedNonEmptyString,
  userProfile: Schema.optionalKey(AuthUserProfile),
  environmentId: EnvironmentId,
  clientProofKeyThumbprint: TrimmedNonEmptyString,
  cnf: Schema.Struct({
    jkt: TrimmedNonEmptyString,
  }),
  deviceId: Schema.optional(TrimmedNonEmptyString),
  nonce: TrimmedNonEmptyString,
  scope: Schema.Array(Schema.Literal("environment:connect")),
});
export type RelayCloudMintCredentialProofPayload = typeof RelayCloudMintCredentialProofPayload.Type;

export const RelayCloudMintCredentialProof = TrimmedNonEmptyString;
export type RelayCloudMintCredentialProof = typeof RelayCloudMintCredentialProof.Type;

export const RelayCloudMintCredentialRequest = Schema.Struct({
  proof: RelayCloudMintCredentialProof,
});
export type RelayCloudMintCredentialRequest = typeof RelayCloudMintCredentialRequest.Type;

export const RelayCloudEnvironmentHealthProofPayload = Schema.Struct({
  ...RelaySignedJwtRegisteredClaims,
  environmentId: EnvironmentId,
  nonce: TrimmedNonEmptyString,
  scope: Schema.Array(Schema.Literal("environment:status")),
});
export type RelayCloudEnvironmentHealthProofPayload =
  typeof RelayCloudEnvironmentHealthProofPayload.Type;

export const RelayCloudEnvironmentHealthProof = TrimmedNonEmptyString;
export type RelayCloudEnvironmentHealthProof = typeof RelayCloudEnvironmentHealthProof.Type;

export const RelayCloudEnvironmentHealthRequest = Schema.Struct({
  proof: RelayCloudEnvironmentHealthProof,
});
export type RelayCloudEnvironmentHealthRequest = typeof RelayCloudEnvironmentHealthRequest.Type;

export const RelayEnvironmentHealthResponseProofPayload = Schema.Struct({
  ...RelaySignedJwtRegisteredClaims,
  environmentId: EnvironmentId,
  requestNonce: TrimmedNonEmptyString,
  status: Schema.Literal("online"),
  descriptor: ExecutionEnvironmentDescriptor,
  checkedAt: TrimmedNonEmptyString,
});
export type RelayEnvironmentHealthResponseProofPayload =
  typeof RelayEnvironmentHealthResponseProofPayload.Type;

export const RelayEnvironmentHealthResponse = Schema.Struct({
  environmentId: EnvironmentId,
  status: Schema.Literal("online"),
  descriptor: ExecutionEnvironmentDescriptor,
  checkedAt: TrimmedNonEmptyString,
  proof: TrimmedNonEmptyString,
});
export type RelayEnvironmentHealthResponse = typeof RelayEnvironmentHealthResponse.Type;

export const RelayEnvironmentMintResponseProofPayload = Schema.Struct({
  ...RelaySignedJwtRegisteredClaims,
  environmentId: EnvironmentId,
  clientProofKeyThumbprint: TrimmedNonEmptyString,
  requestNonce: TrimmedNonEmptyString,
  credential: TrimmedNonEmptyString,
  descriptor: Schema.optionalKey(ExecutionEnvironmentDescriptor),
});
export type RelayEnvironmentMintResponseProofPayload =
  typeof RelayEnvironmentMintResponseProofPayload.Type;

export const RelayEnvironmentMintResponse = Schema.Struct({
  credential: TrimmedNonEmptyString,
  expiresAt: TrimmedNonEmptyString,
  proof: TrimmedNonEmptyString,
});
export type RelayEnvironmentMintResponse = typeof RelayEnvironmentMintResponse.Type;

export const RelayDeliveryKind = Schema.Literals([
  "live_activity_start",
  "live_activity_update",
  "live_activity_end",
  "push_notification",
]);
export type RelayDeliveryKind = typeof RelayDeliveryKind.Type;

export const RelayDeliveryResult = Schema.Struct({
  deviceId: TrimmedNonEmptyString,
  kind: RelayDeliveryKind,
  ok: Schema.Boolean,
  queued: Schema.optional(Schema.Boolean),
  apnsStatus: Schema.NullOr(Schema.Number),
  apnsReason: Schema.NullOr(Schema.String),
  apnsId: Schema.NullOr(Schema.String),
});
export type RelayDeliveryResult = typeof RelayDeliveryResult.Type;

export const RelayOkResponse = Schema.Struct({
  ok: Schema.Boolean,
});
export type RelayOkResponse = typeof RelayOkResponse.Type;

export const RelayBillingCheckoutClient = Schema.Literals(["web"]);
export type RelayBillingCheckoutClient = typeof RelayBillingCheckoutClient.Type;

export const RelayBillingCheckoutResponse = Schema.Struct({
  url: TrimmedNonEmptyString,
});
export type RelayBillingCheckoutResponse = typeof RelayBillingCheckoutResponse.Type;

export const RelayWorkspaceBillingRequest = Schema.Struct({
  organizationId: TrimmedNonEmptyString,
});
export type RelayWorkspaceBillingRequest = typeof RelayWorkspaceBillingRequest.Type;

export const RelayWorkspaceCheckoutRequest = Schema.Struct({
  ...RelayWorkspaceBillingRequest.fields,
  client: RelayBillingCheckoutClient,
});
export type RelayWorkspaceCheckoutRequest = typeof RelayWorkspaceCheckoutRequest.Type;

export const RelayWorkspaceProvisioningStatusResponse = Schema.Struct({
  active: Schema.Boolean,
  ready: Schema.Boolean,
  phase: Schema.optional(
    Schema.Literals([
      "payment_pending",
      "queued",
      "provisioning",
      "ready",
      "failed",
      "unavailable",
    ]),
  ),
});
export type RelayWorkspaceProvisioningStatusResponse =
  typeof RelayWorkspaceProvisioningStatusResponse.Type;

export const RelayPlatformCloudProvider = Schema.Literals(["ovhcloud", "flyio", "webdock", "boat"]);
export type RelayPlatformCloudProvider = typeof RelayPlatformCloudProvider.Type;

export const RelayPlatformCloudConfiguration = Schema.Struct({
  provider: RelayPlatformCloudProvider,
  region: TrimmedNonEmptyString,
  resourceSize: TrimmedNonEmptyString,
  maitoVersion: TrimmedNonEmptyString,
  maxConcurrentAgents: Schema.Int.check(Schema.isGreaterThan(0)),
  maitoImage: TrimmedNonEmptyString,
  boatMainTemplate: Schema.optionalKey(TrimmedNonEmptyString),
  boatWorkerTemplate: Schema.optionalKey(TrimmedNonEmptyString),
  boatRollbackTemplates: Schema.optionalKey(
    Schema.Struct({ main: TrimmedNonEmptyString, worker: TrimmedNonEmptyString }),
  ),
  releaseOperationId: TrimmedNonEmptyString,
  releaseManifestUrl: TrimmedNonEmptyString,
  releaseIdentityRegexp: TrimmedNonEmptyString,
  releaseOidcIssuer: TrimmedNonEmptyString,
  dockerPackageVersion: TrimmedNonEmptyString,
  composePackageVersion: TrimmedNonEmptyString,
});
export type RelayPlatformCloudConfiguration = typeof RelayPlatformCloudConfiguration.Type;

export const RelayPlatformCloudConfigurationUpdate = Schema.Struct({
  ...RelayPlatformCloudConfiguration.fields,
  provisioningSecret: Schema.optional(TrimmedNonEmptyString),
  boatApiKey: Schema.optional(TrimmedNonEmptyString),
});
export type RelayPlatformCloudConfigurationUpdate =
  typeof RelayPlatformCloudConfigurationUpdate.Type;

export const RelayPlatformCloudConfigurationResponse = Schema.Struct({
  configuration: RelayPlatformCloudConfiguration,
  provisioningSecretConfigured: Schema.Boolean,
  boatApiKeyConfigured: Schema.optionalKey(Schema.Boolean),
  source: Schema.Literals(["database", "environment"]),
  updatedAt: Schema.NullOr(TrimmedNonEmptyString),
  updatedByUserId: Schema.NullOr(TrimmedNonEmptyString),
});
export type RelayPlatformCloudConfigurationResponse =
  typeof RelayPlatformCloudConfigurationResponse.Type;

const ReleaseVersion = TrimmedNonEmptyString.check(
  Schema.isPattern(
    /^(0|[1-9][0-9]*)\.(0|[1-9][0-9]*)\.(0|[1-9][0-9]*)(?:-[0-9A-Za-z.-]+)?(?:\+[0-9A-Za-z.-]+)?$/u,
  ),
);

export const RelayPlatformRelease = Schema.Struct({
  version: ReleaseVersion,
  tagName: TrimmedNonEmptyString,
  name: TrimmedNonEmptyString,
  publishedAt: TrimmedNonEmptyString,
  prerelease: Schema.Boolean,
  url: TrimmedNonEmptyString,
  image: TrimmedNonEmptyString,
  manifestUrl: TrimmedNonEmptyString,
  signatureBundleUrl: TrimmedNonEmptyString,
  current: Schema.Boolean,
  boatTemplates: Schema.optionalKey(
    Schema.Struct({ main: TrimmedNonEmptyString, worker: TrimmedNonEmptyString }),
  ),
});
export type RelayPlatformRelease = typeof RelayPlatformRelease.Type;

export const RelayPlatformRollout = Schema.Struct({
  operationId: TrimmedNonEmptyString,
  version: ReleaseVersion,
  image: TrimmedNonEmptyString,
  total: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  pending: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  updating: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  ready: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  rolledBack: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  failed: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  offline: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
});
export type RelayPlatformRollout = typeof RelayPlatformRollout.Type;

export const RelayPlatformReleasesResponse = Schema.Struct({
  releases: Schema.Array(RelayPlatformRelease),
  rollout: Schema.NullOr(RelayPlatformRollout),
  creationEnabled: Schema.Boolean,
});
export type RelayPlatformReleasesResponse = typeof RelayPlatformReleasesResponse.Type;

export const RelayPlatformCreateReleaseRequest = Schema.Struct({ version: ReleaseVersion });
export type RelayPlatformCreateReleaseRequest = typeof RelayPlatformCreateReleaseRequest.Type;

export const RelayPlatformPromoteReleaseParams = Schema.Struct({ version: ReleaseVersion });
export type RelayPlatformPromoteReleaseParams = typeof RelayPlatformPromoteReleaseParams.Type;

export const RelayPlatformReleaseActionResponse = Schema.Struct({ ok: Schema.Literal(true) });

export const RelayPublishResponse = Schema.Struct({
  ok: Schema.Boolean,
  deliveries: Schema.Array(RelayDeliveryResult),
});
export type RelayPublishResponse = typeof RelayPublishResponse.Type;

export const RelayHealthResponse = Schema.Struct({
  ok: Schema.Boolean,
  service: Schema.Literal("relay"),
});
export type RelayHealthResponse = typeof RelayHealthResponse.Type;

export const RelayHealthGroup = HttpApiGroup.make("health")
  .add(
    HttpApiEndpoint.get("health", "/health", {
      success: RelayHealthResponse,
      error: RelayInternalError,
    }).annotate(OpenApi.Summary, "Check relay health"),
  )
  .annotate(OpenApi.Description, "Service health and readiness.");

export const RelayMetadataGroup = HttpApiGroup.make("metadata")
  .add(
    HttpApiEndpoint.get("authorizationServer", "/.well-known/oauth-authorization-server", {
      success: RelayAuthorizationServerMetadata,
    }).annotate(OpenApi.Summary, "Read OAuth authorization-server metadata"),
    HttpApiEndpoint.get("protectedResource", "/.well-known/oauth-protected-resource", {
      success: RelayProtectedResourceMetadata,
    }).annotate(OpenApi.Summary, "Read OAuth protected-resource metadata"),
  )
  .annotate(OpenApi.Description, "OAuth and DPoP discovery metadata.");

export const RelayDirectoryMember = Schema.Struct({
  userId: TrimmedNonEmptyString,
  name: Schema.String,
  identifier: Schema.String,
  imageUrl: Schema.String,
});
export const RelayDirectoryResponse = Schema.Array(RelayDirectoryMember);

export const RelayDirectoryGroup = HttpApiGroup.make("directory")
  .add(
    HttpApiEndpoint.get("searchMembers", "/v1/client/members", {
      headers: RelayBearerRequestHeaders,
      query: Schema.Struct({
        query: Schema.optional(Schema.String.check(Schema.isMaxLength(256))),
      }),
      success: RelayDirectoryResponse,
      error: RelayAuthAndInternalErrors,
    }),
  )
  .middleware(RelayClientAuth);

export const RelayClientGroup = HttpApiGroup.make("client")
  .add(
    HttpApiEndpoint.get("listCloudEnvironments", "/v1/client/cloud-environments", {
      headers: RelayBearerRequestHeaders,
      success: Schema.Array(CloudEnvironment),
      error: RelayAuthAndInternalErrors,
    }),
    HttpApiEndpoint.post("createCloudEnvironment", "/v1/client/cloud-environments", {
      headers: RelayBearerRequestHeaders,
      payload: CloudEnvironmentCreateRequest,
      success: CloudEnvironment,
      error: [...RelayAuthAndInternalErrors, CloudEnvironmentForbidden, CloudEnvironmentConflict],
    }),
    HttpApiEndpoint.get("getCloudEnvironment", "/v1/client/cloud-environments/:environmentId", {
      headers: RelayBearerRequestHeaders,
      params: RelayEnvironmentUnlinkParams,
      success: CloudEnvironment,
      error: [...RelayAuthAndInternalErrors, CloudEnvironmentNotFound],
    }),
    HttpApiEndpoint.post(
      "requestCloudEnvironmentAction",
      "/v1/client/cloud-environments/:environmentId/actions",
      {
        headers: RelayBearerRequestHeaders,
        params: RelayEnvironmentUnlinkParams,
        payload: CloudEnvironmentActionRequest,
        success: CloudEnvironment,
        error: [
          ...RelayAuthAndInternalErrors,
          CloudEnvironmentForbidden,
          CloudEnvironmentNotFound,
          CloudEnvironmentConflict,
        ],
      },
    ),
    HttpApiEndpoint.get("listEnvironments", "/v1/environments", {
      headers: RelayBearerRequestHeaders,
      success: RelayListEnvironmentsResponse,
      error: RelayAuthAndInternalErrors,
    }).annotate(OpenApi.Summary, "List linked environments"),
    HttpApiEndpoint.post("inviteWorkspaceMember", "/v1/client/workspace-invitations", {
      headers: RelayBearerRequestHeaders,
      payload: RelayWorkspaceInvitationRequest,
      success: RelayOkResponse,
      error: RelayAuthAndInternalErrors,
    }).annotate(OpenApi.Summary, "Invite a member to the active workspace"),
    HttpApiEndpoint.post("linkEnvironment", "/v1/client/environment-links", {
      headers: RelayBearerRequestHeaders,
      payload: RelayEnvironmentLinkRequest,
      success: RelayEnvironmentLinkResponse,
      error: RelayEnvironmentLinkErrors,
    }).annotate(OpenApi.Summary, "Link an environment"),
    HttpApiEndpoint.post(
      "createEnvironmentLinkChallenge",
      "/v1/client/environment-link-challenges",
      {
        headers: RelayBearerRequestHeaders,
        payload: RelayEnvironmentLinkChallengeRequest,
        success: RelayEnvironmentLinkChallengeResponse,
        error: RelayAuthAndInternalErrors,
      },
    ).annotate(OpenApi.Summary, "Create an environment-link challenge"),
    HttpApiEndpoint.delete("unlinkEnvironment", "/v1/client/environment-links/:environmentId", {
      headers: RelayBearerRequestHeaders,
      params: RelayEnvironmentUnlinkParams,
      success: RelayOkResponse,
      error: RelayAuthAndInternalErrors,
    }).annotate(OpenApi.Summary, "Unlink an environment"),
    HttpApiEndpoint.patch("renameEnvironment", "/v1/client/environment-links/:environmentId", {
      headers: RelayBearerRequestHeaders,
      params: RelayEnvironmentUnlinkParams,
      payload: RelayEnvironmentRenameRequest,
      success: RelayOkResponse,
      error: RelayAuthAndInternalErrors,
    }).annotate(OpenApi.Summary, "Rename a linked environment"),
    HttpApiEndpoint.delete(
      "releaseEnvironmentTunnel",
      "/v1/client/environment-links/:environmentId/tunnel",
      {
        headers: RelayBearerRequestHeaders,
        params: RelayEnvironmentUnlinkParams,
        success: RelayOkResponse,
        error: RelayAuthAndInternalErrors,
      },
    )
      .annotate(OpenApi.Summary, "Release an environment's managed tunnel")
      .annotate(
        OpenApi.Description,
        "Deletes the provisioned Cloudflare tunnel while keeping the environment link and its hostname reservation, so a later link re-provisions the tunnel under the same URL. Environments call this when they shut down; Cloudflare bills per provisioned tunnel, so idle tunnels should not outlive their environment.",
      ),
  )
  .annotate(OpenApi.Description, "Cloud-user environment links and registered devices.")
  .middleware(RelayClientAuth);

export const RelayAccountGroup = HttpApiGroup.make("account")
  .add(
    HttpApiEndpoint.post("createWorkspaceBillingPortal", "/v1/account/workspaces/portal", {
      headers: RelayBearerRequestHeaders,
      payload: RelayWorkspaceBillingRequest,
      success: RelayBillingCheckoutResponse,
      error: RelayAuthAndInternalErrors,
    }).annotate(OpenApi.Summary, "Open the workspace's Stripe customer portal"),
    HttpApiEndpoint.post("createWorkspaceCheckout", "/v1/account/workspaces/checkout", {
      headers: RelayBearerRequestHeaders,
      payload: RelayWorkspaceCheckoutRequest,
      success: RelayBillingCheckoutResponse,
      error: RelayAuthAndInternalErrors,
    }).annotate(OpenApi.Summary, "Create Checkout for a workspace being created"),
    HttpApiEndpoint.post("getWorkspaceProvisioningStatus", "/v1/account/workspaces/status", {
      headers: RelayBearerRequestHeaders,
      payload: RelayWorkspaceBillingRequest,
      success: RelayWorkspaceProvisioningStatusResponse,
      error: RelayAuthAndInternalErrors,
    }).annotate(OpenApi.Summary, "Get billing and provisioning status for a workspace"),
    HttpApiEndpoint.post("setupWorkspace", "/v1/account/workspaces/setup", {
      headers: RelayBearerRequestHeaders,
      payload: RelayWorkspaceBillingRequest,
      success: RelayOkResponse,
      error: [...RelayAuthAndInternalErrors, CloudEnvironmentForbidden],
    }).annotate(OpenApi.Summary, "Start paid workspace setup before workspace activation"),
  )
  .annotate(OpenApi.Description, "Signed-in account operations before workspace activation.")
  .middleware(RelayAccountAuth);

export const RelayPlatformAdminGroup = HttpApiGroup.make("platformAdmin")
  .add(
    HttpApiEndpoint.get("getCloudConfiguration", "/v1/platform-admin/cloud-configuration", {
      headers: RelayBearerRequestHeaders,
      success: RelayPlatformCloudConfigurationResponse,
      error: [RelayAuthInvalidError, RelayPlatformAdminRequiredError, RelayInternalError],
    }).annotate(OpenApi.Summary, "Read platform managed-cloud configuration"),
    HttpApiEndpoint.put("updateCloudConfiguration", "/v1/platform-admin/cloud-configuration", {
      headers: RelayBearerRequestHeaders,
      payload: RelayPlatformCloudConfigurationUpdate,
      success: RelayPlatformCloudConfigurationResponse,
      error: [RelayAuthInvalidError, RelayPlatformAdminRequiredError, RelayInternalError],
    }).annotate(OpenApi.Summary, "Update platform managed-cloud configuration"),
    HttpApiEndpoint.get("listPlatformReleases", "/v1/platform-admin/releases", {
      headers: RelayBearerRequestHeaders,
      success: RelayPlatformReleasesResponse,
      error: [RelayAuthInvalidError, RelayPlatformAdminRequiredError, RelayInternalError],
    }).annotate(OpenApi.Summary, "List signed backend releases and rollout state"),
    HttpApiEndpoint.post("createPlatformRelease", "/v1/platform-admin/releases", {
      headers: RelayBearerRequestHeaders,
      payload: RelayPlatformCreateReleaseRequest,
      success: RelayPlatformReleaseActionResponse,
      error: [RelayAuthInvalidError, RelayPlatformAdminRequiredError, RelayInternalError],
    }).annotate(OpenApi.Summary, "Dispatch the backend release workflow"),
    HttpApiEndpoint.post("promotePlatformRelease", "/v1/platform-admin/releases/:version/promote", {
      headers: RelayBearerRequestHeaders,
      params: RelayPlatformPromoteReleaseParams,
      success: RelayPlatformReleaseActionResponse,
      error: [RelayAuthInvalidError, RelayPlatformAdminRequiredError, RelayInternalError],
    }).annotate(OpenApi.Summary, "Promote or roll back the self-hosted backend fleet"),
    HttpApiEndpoint.post(
      "retirePlatformReleaseTemplates",
      "/v1/platform-admin/releases/:version/retire-templates",
      {
        headers: RelayBearerRequestHeaders,
        params: RelayPlatformPromoteReleaseParams,
        success: RelayPlatformReleaseActionResponse,
        error: [RelayAuthInvalidError, RelayPlatformAdminRequiredError, RelayInternalError],
      },
    ).annotate(OpenApi.Summary, "Retire old Boat release templates while retaining rollback"),
  )
  .annotate(OpenApi.Description, "Platform-administrator operations.")
  .middleware(RelayClientAuth);

export const RelayCloudEnrollmentGroup = HttpApiGroup.make("cloudEnrollment").add(
  HttpApiEndpoint.post("createCloudEnrollmentChallenge", "/v1/cloud/enrollment/challenge", {
    payload: CloudEnrollmentCredential,
    success: RelayEnvironmentLinkChallengeResponse,
    error: [CloudEnrollmentInvalid, RelayInternalError],
  }),
  HttpApiEndpoint.post("enrollCloudEnvironment", "/v1/cloud/enrollment", {
    payload: CloudEnrollmentRequest,
    success: RelayEnvironmentLinkResponse,
    error: [CloudEnrollmentInvalid, RelayInternalError],
  }),
  HttpApiEndpoint.post("completeCloudEnrollment", "/v1/cloud/enrollment/ready", {
    payload: CloudEnrollmentReadyRequest,
    success: CloudEnvironment,
    error: [CloudEnrollmentInvalid, RelayInternalError],
  }),
  HttpApiEndpoint.post("getCloudUpdaterDesiredRelease", "/v1/cloud/updater/desired", {
    headers: CloudUpdaterRequestHeaders,
    payload: CloudUpdaterDesiredRequest,
    success: CloudUpdaterDesiredResponse,
    error: [CloudEnrollmentInvalid, RelayInternalError],
  }),
  HttpApiEndpoint.post("reportCloudUpdaterState", "/v1/cloud/updater/report", {
    headers: CloudUpdaterRequestHeaders,
    payload: CloudUpdaterReportRequest,
    success: RelayOkResponse,
    error: [CloudEnrollmentInvalid, RelayInternalError],
  }),
);

export const RelayExchangeDpopAccessTokenEndpoint = HttpApiEndpoint.post(
  "exchangeDpopAccessToken",
  "/v1/client/dpop-token",
  {
    headers: RelayDpopProofRequestHeaders,
    payload: RelayDpopAccessTokenRequest,
    success: RelayDpopAccessTokenResponse,
    error: RelayAuthAndInternalErrors,
  },
)
  .annotate(OpenApi.Summary, "Exchange a Clerk token for a DPoP access token")
  .annotate(
    OpenApi.Description,
    "Bootstrap endpoint. Send the DPoP proof JWT in the dpop header and the Clerk token in subject_token. The returned access token is bound to the proof key.",
  );

export const RelayTokenGroup = HttpApiGroup.make("token")
  .add(RelayExchangeDpopAccessTokenEndpoint)
  .annotate(OpenApi.Description, "OAuth token exchange for DPoP-bound client access.");

export const RelayConnectEnvironmentEndpoint = HttpApiEndpoint.post(
  "connectEnvironment",
  "/v1/environments/:environmentId/connect",
  {
    headers: RelayDpopRequestHeaders,
    params: Schema.Struct({
      environmentId: EnvironmentId,
    }),
    payload: RelayEnvironmentConnectRequest,
    success: RelayEnvironmentConnectResponse,
    error: RelayEnvironmentConnectErrors,
  },
).annotate(OpenApi.Summary, "Connect to an environment");

export const RelayGetEnvironmentStatusEndpoint = HttpApiEndpoint.post(
  "getEnvironmentStatus",
  "/v1/environments/:environmentId/status",
  {
    headers: RelayDpopRequestHeaders,
    params: Schema.Struct({
      environmentId: EnvironmentId,
    }),
    success: RelayEnvironmentStatusResponse,
    error: RelayEnvironmentConnectErrors,
  },
).annotate(OpenApi.Summary, "Check environment status");

export const RelayDpopClientGroup = HttpApiGroup.make("dpopClient")
  .add(RelayConnectEnvironmentEndpoint, RelayGetEnvironmentStatusEndpoint)
  .annotate(OpenApi.Description, "DPoP-authenticated client access to linked environments.")
  .middleware(RelayDpopClientAuth);

export const RelayServerGroup = HttpApiGroup.make("server")
  .add(
    HttpApiEndpoint.post("createOAuthAttempt", "/v1/environments/:environmentId/oauth/attempts", {
      params: Schema.Struct({
        environmentId: EnvironmentId,
      }),
      payload: RelayOAuthAttemptCreateRequest,
      success: RelayOAuthAttemptCreateResponse,
      error: RelayAuthAndInternalErrors,
    }).annotate(OpenApi.Summary, "Create a routed OAuth attempt"),
  )
  .annotate(OpenApi.Description, "Environment-authenticated relay operations.")
  .middleware(RelayEnvironmentAuth);

export const RelayCreditAmount = Schema.Int.check(
  Schema.isGreaterThan(0),
  Schema.isLessThanOrEqualTo(2_147_483_647),
);
export const RelayCreditOperationId = TrimmedNonEmptyString.check(Schema.isMaxLength(200));
export class RelayCreditError extends Schema.TaggedErrorClass<RelayCreditError>()(
  "RelayCreditError",
  { code: Schema.Literal("insufficient_credits") },
  { httpApiStatus: 402 },
) {}
export const RelayCreditReloadState = Schema.Struct({
  enabled: Schema.Boolean,
  status: Schema.Literals(["disabled", "setup_pending", "enabled", "needs_attention"]),
  paymentPending: Schema.Boolean,
});
export const RelayComputeFunding = Schema.Struct({
  balanceUnavailable: Schema.optionalKey(Schema.Boolean),
  pendingChargeCredits: Schema.optionalKey(
    Schema.Number.check(Schema.isFinite(), Schema.isGreaterThanOrEqualTo(0)),
  ),
  availableCredits: Schema.Number.check(Schema.isFinite(), Schema.isGreaterThanOrEqualTo(0)),
  requiredCredits: Schema.Number.check(Schema.isFinite(), Schema.isGreaterThan(0)),
  autoReload: RelayCreditReloadState,
  topUpUrl: Schema.optionalKey(Schema.String),
});
export type RelayComputeFunding = typeof RelayComputeFunding.Type;
export const RelayCreditBalance = Schema.Struct({
  organizationId: Schema.String,
  balanceCredits: Schema.Number.check(Schema.isFinite()),
  purchasedCredits: Schema.Number.check(Schema.isFinite()),
  subscriptionCredits: Schema.Number.check(Schema.isFinite()),
  subscriptionAllowanceCredits: Schema.NullOr(Schema.Int),
  subscriptionPeriodEnd: Schema.NullOr(Schema.Int),
  purchaseMinimumCredits: Schema.optionalKey(Schema.Int),
  reservedCredits: Schema.optionalKey(Schema.Number.check(Schema.isFinite())),
  spentCredits: Schema.optionalKey(Schema.Number.check(Schema.isFinite())),
  pendingChargeCredits: Schema.optionalKey(Schema.Number.check(Schema.isFinite())),
  autoReload: Schema.optionalKey(RelayCreditReloadState),
  topUpUrl: Schema.optionalKey(Schema.String),
  currency: Schema.Literal("usd"),
  centsPerCredit: Schema.Literal(0.1),
  entries: Schema.Array(
    Schema.Struct({
      operationId: Schema.String,
      amount: Schema.Number.check(Schema.isFinite()),
      purchasedAmount: Schema.Number.check(Schema.isFinite()),
      subscriptionAmount: Schema.Number.check(Schema.isFinite()),
      feature: Schema.String,
      environmentId: Schema.NullOr(Schema.String),
      actor: Schema.String,
      reason: Schema.String,
      chargeOperationId: Schema.NullOr(Schema.String),
      createdAt: Schema.String,
    }),
  ),
});
export type RelayCreditBalance = typeof RelayCreditBalance.Type;
export const RelayCreditGrant = Schema.Struct({
  organizationId: TrimmedNonEmptyString.check(Schema.isMaxLength(191)),
  operationId: RelayCreditOperationId,
  amount: RelayCreditAmount,
  reason: TrimmedNonEmptyString.check(Schema.isMaxLength(500)),
});
export type RelayCreditGrant = typeof RelayCreditGrant.Type;
export const RelayCreditRefund = Schema.Struct({
  organizationId: RelayCreditGrant.fields.organizationId,
  operationId: RelayCreditOperationId,
  chargeOperationId: RelayCreditOperationId,
  reason: RelayCreditGrant.fields.reason,
});
export type RelayCreditRefund = typeof RelayCreditRefund.Type;
export const RelayCreditsGroup = HttpApiGroup.make("credits")
  .add(
    HttpApiEndpoint.get("getCredits", "/v1/client/credits", {
      success: RelayCreditBalance,
      error: HttpApiError.ServiceUnavailable,
    }),
    HttpApiEndpoint.post("grantCredits", "/v1/platform/credits/grants", {
      payload: RelayCreditGrant,
      success: RelayCreditBalance,
      error: [
        RelayPlatformAdminRequiredError,
        HttpApiError.Conflict,
        HttpApiError.ServiceUnavailable,
      ],
    }),
    HttpApiEndpoint.post("refundCredits", "/v1/platform/credits/refunds", {
      payload: RelayCreditRefund,
      success: RelayCreditBalance,
      error: [
        RelayPlatformAdminRequiredError,
        HttpApiError.Conflict,
        HttpApiError.ServiceUnavailable,
      ],
    }),
  )
  .middleware(RelayClientAuth);
export const RelayCreditPurchaseRequest = Schema.Struct({
  credits: Schema.Int.check(
    Schema.isGreaterThan(0),
    Schema.isLessThanOrEqualTo(10_000_000),
    Schema.isMultipleOf(10),
  ),
  operationId: TrimmedNonEmptyString.check(Schema.isMaxLength(40)),
  client: RelayBillingCheckoutClient,
});
export type RelayCreditPurchaseRequest = typeof RelayCreditPurchaseRequest.Type;
export const RelayAutoTopUpAmount = Schema.Literals([20000, 50000, 100000]);
export const RelayCreditAutoTopUp = Schema.Struct({
  enabled: Schema.Boolean,
  credits: RelayAutoTopUpAmount,
  thresholdCredits: Schema.Literal(1000),
  status: Schema.Literals(["disabled", "setup_pending", "enabled", "needs_attention"]),
  cardLast4: Schema.NullOr(Schema.String),
  paymentPending: Schema.Boolean,
});
export type RelayCreditAutoTopUp = typeof RelayCreditAutoTopUp.Type;
export const RelayCreditAutoTopUpConfiguration = Schema.Struct({
  enabled: Schema.Boolean,
  credits: RelayAutoTopUpAmount,
});
export type RelayCreditAutoTopUpConfiguration = typeof RelayCreditAutoTopUpConfiguration.Type;
export const RelayCreditAutoTopUpsGroup = HttpApiGroup.make("creditAutoTopUps")
  .add(
    HttpApiEndpoint.get("getCreditAutoTopUp", "/v1/client/credits/auto-top-up", {
      success: RelayCreditAutoTopUp,
      error: HttpApiError.ServiceUnavailable,
    }),
    HttpApiEndpoint.post("createCreditAutoTopUpSetup", "/v1/client/credits/auto-top-up/setup", {
      payload: Schema.Struct({
        ...RelayCreditPurchaseRequest.fields,
        credits: RelayAutoTopUpAmount,
      }),
      success: RelayBillingCheckoutResponse,
      error: [
        HttpApiError.BadRequest,
        HttpApiError.Conflict,
        HttpApiError.Forbidden,
        HttpApiError.ServiceUnavailable,
      ],
    }),
    HttpApiEndpoint.patch("configureCreditAutoTopUp", "/v1/client/credits/auto-top-up", {
      payload: RelayCreditAutoTopUpConfiguration,
      success: RelayCreditAutoTopUp,
      error: [
        HttpApiError.BadRequest,
        HttpApiError.Conflict,
        HttpApiError.Forbidden,
        HttpApiError.ServiceUnavailable,
      ],
    }),
  )
  .middleware(RelayClientAuth);
export const RelayCreditPurchasesGroup = HttpApiGroup.make("creditPurchases")
  .add(
    HttpApiEndpoint.post("createCreditCheckout", "/v1/client/credits/checkout", {
      payload: RelayCreditPurchaseRequest,
      success: RelayBillingCheckoutResponse,
      error: [HttpApiError.BadRequest, HttpApiError.Forbidden, HttpApiError.ServiceUnavailable],
    }),
  )
  .middleware(RelayClientAuth);
export const RelayEnvironmentCreditsGroup = HttpApiGroup.make("environmentCredits")
  .add(
    HttpApiEndpoint.get("getEnvironmentCredits", "/v1/environments/:environmentId/credits", {
      params: Schema.Struct({ environmentId: EnvironmentId }),
      success: RelayCreditBalance,
      error: [HttpApiError.Forbidden, HttpApiError.ServiceUnavailable],
    }),
  )
  .middleware(RelayEnvironmentAuth);

export const RelaySearchRequest = Schema.Struct({
  operationId: RelayCreditOperationId,
  query: TrimmedNonEmptyString.check(Schema.isMaxLength(2000)),
});

export const RelaySearchResponse = Schema.Struct({
  results: Schema.Array(
    Schema.Struct({
      title: Schema.String,
      url: Schema.String,
      content: Schema.optional(Schema.String),
    }),
  ),
  unresponsive_engines: Schema.optional(Schema.Array(Schema.Tuple([Schema.String, Schema.String]))),
});

export const RelaySearchGroup = HttpApiGroup.make("search")
  .add(
    HttpApiEndpoint.post("searchWeb", "/v1/environments/:environmentId/search", {
      params: Schema.Struct({ environmentId: EnvironmentId }),
      payload: RelaySearchRequest,
      success: RelaySearchResponse,
      error: [
        RelayCreditError,
        HttpApiError.Conflict,
        HttpApiError.Forbidden,
        HttpApiError.ServiceUnavailable,
      ],
    }),
  )
  .middleware(RelayEnvironmentAuth);

export const RelaySourceControlGroup = HttpApiGroup.make("sourceControl")
  .add(
    HttpApiEndpoint.post("sourceControlAction", "/v1/environments/:environmentId/source-control", {
      params: Schema.Struct({ environmentId: EnvironmentId }),
      payload: RelaySourceControlAction,
      success: RelaySourceControlResult,
      error: [RelaySourceControlError, HttpApiError.Forbidden, HttpApiError.ServiceUnavailable],
    }),
  )
  .middleware(RelayEnvironmentAuth);

/** Requests are authorized against relay-owned worker receipts; never return the platform key. */
export const RelayBoatRequest = Schema.Struct({
  method: Schema.Literals(["GET", "POST", "PUT", "DELETE"]),
  path: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(4096)),
  body: Schema.optionalKey(Schema.Unknown),
  operationId: Schema.optionalKey(TrimmedNonEmptyString.check(Schema.isMaxLength(256))),
  timeoutMs: Schema.optionalKey(
    Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 650000 })),
  ),
});
export type RelayBoatRequest = typeof RelayBoatRequest.Type;
/** A null ID means the allocation outcome is unknown, not that no VM exists. */
export const RelayBoatAllocation = Schema.Struct({
  sandboxId: Schema.NullOr(TrimmedNonEmptyString),
});
export const RelayBoatGroup = HttpApiGroup.make("boat")
  .add(
    HttpApiEndpoint.post("workerRequest", "/v1/environments/:environmentId/boat", {
      params: Schema.Struct({ environmentId: EnvironmentId }),
      payload: RelayBoatRequest,
      success: Schema.Unknown,
      error: [HttpApiError.Forbidden, HttpApiError.ServiceUnavailable],
    }),
  )
  .middleware(RelayEnvironmentAuth);

export const RelayEmailGroup = HttpApiGroup.make("email")
  .add(
    HttpApiEndpoint.post("action", "/v1/environments/:environmentId/email", {
      params: Schema.Struct({ environmentId: EnvironmentId }),
      payload: RelayEmailAction,
      success: RelayEmailResult,
      error: [HttpApiError.Forbidden, HttpApiError.ServiceUnavailable],
    }),
  )
  .middleware(RelayEnvironmentAuth);

export const RelayApi = HttpApi.make("RelayApi")
  .add(
    RelayEmailGroup,
    RelayBoatGroup,
    RelaySourceControlGroup,
    RelayCreditsGroup,
    RelayCreditPurchasesGroup,
    RelayCreditAutoTopUpsGroup,
    RelayEnvironmentCreditsGroup,
    RelaySearchGroup,
    RelayHealthGroup,
    RelayMetadataGroup,
    RelayAccountGroup,
    RelayClientGroup,
    RelayDirectoryGroup,
    RelayPlatformAdminGroup,
    RelayCloudEnrollmentGroup,
    RelayTokenGroup,
    RelayDpopClientGroup,
    RelayServerGroup,
  )
  .annotate(OpenApi.Title, "Maito Relay API")
  .annotate(OpenApi.Version, "1.0.0")
  .annotate(
    OpenApi.Description,
    "Control-plane API for linking Maito environments, connecting authorized clients, and publishing agent activity.",
  );
export type RelayApi = typeof RelayApi;
